import { Popover } from "@headlessui/react"
import classNames from "classnames"
import { formatISO } from "date-fns"
import React, { useContext, useMemo } from "react"
import { useQuery } from "react-query"
import { BoaterInvoicesContext } from "src/main/Account/Invoices"
import {
  getDateFilterText,
  handleEndDateChange,
  handleStartDateChange,
} from "src/main/Account/Invoices/InvoiceList/helpers"
import { parseDateValue } from "src/main/Billing/helpers"

import Chips from "src/components/Chips"
import DropdownCheckboxSelect from "src/components/DropdownCheckboxSelect"
import Form from "src/components/Form"
import ReloadableWidget from "src/components/ReloadableWidget"

import { queryUserInvoices } from "src/api/Account/Invoices"

import { RailsArray, useQueryParamState } from "src/hooks/use_query_param_state"

import InvoiceGroup from "./InvoiceGroup"

const InvoiceList = () => {
  const { invoiceFilters } = useContext(BoaterInvoicesContext)
  const [statusFilters, setStatusFilters] = useQueryParamState("statuses", {
    type: RailsArray,
    defaultValue: [],
  })
  const [marinaFilters, setMarinaFilters] = useQueryParamState("marinas", {
    type: RailsArray,
    defaultValue: [],
  })
  const [startDateString, setStartDateString] = useQueryParamState(
    "due_date_gteq",
    { type: String, defaultValue: "" }
  )
  const [endDateString, setEndDateString] = useQueryParamState(
    "due_date_lteq",
    { type: String, defaultValue: "" }
  )
  const [typeFilters, setTypeFilters] = useQueryParamState("types", {
    type: RailsArray,
    defaultValue: [],
  })
  const startDate = startDateString ? parseDateValue(startDateString) : null
  const endDate = endDateString ? parseDateValue(endDateString) : null
  const hasFilters =
    statusFilters.length > 0 ||
    startDate ||
    endDate ||
    marinaFilters.length > 0 ||
    typeFilters.length > 0

  const queryParams = useMemo(() => {
    const params = {}

    if (statusFilters.length > 0) {
      params.statuses = statusFilters
    }
    if (marinaFilters.length > 0) {
      params.marinas = marinaFilters
    }
    if (typeFilters.length > 0) {
      params.types = typeFilters
    }

    // Convert date filters to ISO strings
    if (startDate) {
      params.dueDateGteq = formatISO(startDate, { representation: "date" })
    }
    if (endDate) {
      params.dueDateLteq = formatISO(endDate, { representation: "date" })
    }

    return params
  }, [statusFilters, startDate, endDate, marinaFilters, typeFilters])

  const {
    data: invoiceData,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["userInvoices", queryParams],
    queryFn: () => queryUserInvoices(queryParams),
    placeholderData: [],
    retry: false,
  })

  const handleFilterSelect = (filters, value, setFilters) => {
    const newFilters = filters.includes(value)
      ? filters.filter((v) => v !== value)
      : [...filters, value]
    setFilters(newFilters)
  }

  const renderDateFilterDropdown = (label) => (
    <Popover as="div" className="relative w-full sm:w-auto">
      {({ open }) => (
        <>
          <Popover.Button
            className={`btn-secondary flex w-full items-center whitespace-nowrap focus:outline-none sm:w-auto`}
          >
            <span>{label}</span>
            <i
              className={classNames("fa-solid ml-2 text-xxxs", {
                "fa-chevron-down": !open,
                "fa-chevron-up": open,
              })}
            />
          </Popover.Button>
          <Popover.Panel className="absolute left-0 z-10 w-48 origin-top-left border bg-white p-4 shadow-lg">
            <div className="space-y-2">
              <div className="flex flex-col">
                <Form.Label htmlFor="start-date">Start Date</Form.Label>
                <Form.DatePicker
                  id="start-date"
                  value={startDate}
                  onChange={(date) =>
                    handleStartDateChange(date, setStartDateString)
                  }
                  maxDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  todayButton="Today"
                />
              </div>
              <div className="flex flex-col">
                <Form.Label htmlFor="end-date">End Date</Form.Label>
                <Form.DatePicker
                  id="end-date"
                  value={endDate}
                  onChange={(date) =>
                    handleEndDateChange(date, setEndDateString)
                  }
                  minDate={startDate}
                  dateFormat="MM/dd/yyyy"
                  todayButton="Today"
                />
              </div>
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  )

  const renderFilterSelectDropdown = (label, options, selected, setter) => (
    <DropdownCheckboxSelect
      label={label}
      options={options}
      selected={selected}
      onChange={(event) =>
        handleFilterSelect(selected, event.target.id, setter)
      }
    />
  )

  const renderDateChip = () => {
    if (!startDate && !endDate) return null

    return (
      <Chips.Chip
        text={getDateFilterText(startDate, endDate)}
        onClick={() => {
          setStartDateString("")
          setEndDateString("")
        }}
      />
    )
  }

  const renderChipsForSelectFilter = (
    kind,
    appliedFilters,
    options,
    setter
  ) => {
    return appliedFilters.map((appliedFilter) => {
      const label = options.find((opt) => opt.value === appliedFilter)?.label
      const chipKey = `${kind}-filter-chip-${appliedFilter}`
      return (
        <Chips.Chip
          key={chipKey}
          text={label}
          onClick={() =>
            handleFilterSelect(appliedFilters, appliedFilter, setter)
          }
          data-testid={chipKey}
        />
      )
    })
  }

  const renderFilters = () => (
    <div className="grid grid-cols-1 gap-2 sm:flex sm:grid-cols-none">
      <div className="flex gap-2 sm:col-span-1">
        {renderFilterSelectDropdown(
          invoiceFilters.status.label,
          invoiceFilters.status.options,
          statusFilters,
          setStatusFilters
        )}
        {renderFilterSelectDropdown(
          invoiceFilters.marina.label,
          invoiceFilters.marina.options,
          marinaFilters,
          setMarinaFilters
        )}
      </div>
      <div className="flex gap-2 sm:col-span-1">
        {renderDateFilterDropdown(invoiceFilters.dueDate.label)}
        {renderFilterSelectDropdown(
          invoiceFilters.type.label,
          invoiceFilters.type.options,
          typeFilters,
          setTypeFilters
        )}
      </div>
    </div>
  )

  const renderChips = () => {
    if (!hasFilters) return null

    return (
      <div>
        <Chips onReset={handleClearFilters}>
          {renderChipsForSelectFilter(
            "status",
            statusFilters,
            invoiceFilters.status.options,
            setStatusFilters
          )}
          {renderChipsForSelectFilter(
            "marina",
            marinaFilters,
            invoiceFilters.marina.options,
            setMarinaFilters
          )}
          {renderDateChip()}
          {renderChipsForSelectFilter(
            "type",
            typeFilters,
            invoiceFilters.type.options,
            setTypeFilters
          )}
        </Chips>
      </div>
    )
  }

  const handleClearFilters = () => {
    setStatusFilters([])
    setMarinaFilters([])
    setStartDateString("")
    setEndDateString("")
    setTypeFilters([])
  }

  return (
    <div className="grid grid-cols-1 gap-6 text-base">
      <div>
        {renderFilters()}
        {renderChips()}
      </div>
      <ReloadableWidget isLoading={isFetching} isError={isError}>
        <div className="mb-10 grid gap-6">
          {(invoiceData?.invoiceGroups || []).map((invoiceGroup) => (
            <InvoiceGroup
              key={invoiceGroup.key}
              groupKey={invoiceGroup.key}
              invoices={invoiceGroup.invoices}
              heading={invoiceGroup.heading}
              subheading={
                hasFilters
                  ? invoiceGroup.filteredSubheading
                  : invoiceGroup.unfilteredSubheading
              }
            />
          ))}
        </div>
      </ReloadableWidget>
    </div>
  )
}

export default InvoiceList
